import { computed, Ref } from 'vue'
import type { MultiselectOption } from '@ironsight/types'
import { useGetters } from 'vuex-composition-helpers'
import Division from '@/models/division'
import { BusinessUnit, Hub, Organization } from '@/models/business-unit'
import Company from '@/models/company'
import { useGetCompanies } from '@/api/companies'
import { useGetSelectableHubs } from '@/api/hubs'

export function useBusinessUnitFilter(): {
  options: Ref<MultiselectOption[]>
} {
  const { getAllBusinessUnits } = useGetters('businessUnits', ['getAllBusinessUnits'])

  const options = computed<MultiselectOption[]>(() => {
    const businessUnits = Object.values(
      getAllBusinessUnits.value as Record<string, BusinessUnit>,
    ).map((bu) => ({
      label: bu.name,
      value: bu.id,
    }))

    return businessUnits.sort((a, b) => {
      if (a.value === 'null') return -1
      if (b.value === 'null') return 1
      return a.label.localeCompare(b.label)
    })
  })

  return { options }
}

export function useCompanyFilter(): {
  options: Ref<MultiselectOption[]>
} {
  const { getAllCompanies } = useGetters('companies', ['getAllCompanies'])

  const options = computed<MultiselectOption[]>(() => {
    const companies = Object.values(getAllCompanies.value as Record<string, Company>).map(
      (company) => ({
        label: company.name,
        value: company.id,
      }),
    )

    return companies.sort((a, b) => {
      if (a.value === 'null') return -1
      if (b.value === 'null') return 1
      return a.label.localeCompare(b.label)
    })
  })

  return { options }
}

export function useDivisionFilter(): {
  options: Ref<MultiselectOption[]>
} {
  const { getAllDivisions, getAllHubs } = useGetters('businessUnits', [
    'getAllDivisions',
    'getAllHubs',
  ])

  const options = computed<MultiselectOption[]>(() => {
    const hubs = getAllHubs.value
    return getAllDivisions.value
      .map((division: Division) => {
        const ownerHub = hubs.find((hub) => hub.id === division.ownerId)
        const hubName = ownerHub ? ownerHub.name : ''

        return {
          hubName,
          divisionName: division.name,
          label: division.name,
          value: division.id,
          description: hubName || undefined,
        }
      })
      .sort((a, b) => {
        const hubNameCompare = a.hubName.toLowerCase().localeCompare(b.hubName.toLowerCase())
        if (hubNameCompare === 0) {
          return (
            a.divisionName &&
            b.divisionName &&
            a.divisionName.toLowerCase().localeCompare(b.divisionName?.toLowerCase())
          )
        }
        return hubNameCompare
      })
      .map(({ label, value, description }) => ({ label, value, description }))
  })

  return { options }
}

export function useHubFilter(): {
  options: Ref<MultiselectOption[]>
} {
  const { data: selectableHubs } = useGetSelectableHubs()

  const options = computed<MultiselectOption[]>(() => {
    const hubs = selectableHubs.value ?? []
    return hubs
      .map((hub) => ({
        label: hub.name,
        value: hub.id,
      }))
      .sort((a: MultiselectOption, b: MultiselectOption) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
      )
  })

  return { options }
}

export function useServiceProviderFilter(): {
  options: Ref<MultiselectOption[]>
} {
  const { getAllOrganizations } = useGetters('businessUnits', ['getAllOrganizations'])

  const options = computed<MultiselectOption[]>(() => {
    const providers = Object.values(getAllOrganizations.value as Record<string, Organization>).map(
      (org) => ({
        label: org.name,
        value: org.id,
      }),
    )

    const allOptions: MultiselectOption[] = [{ label: 'Unassigned', value: 'null' }, ...providers]

    return allOptions.sort((a, b) => {
      if (a.value === 'null') return -1
      if (b.value === 'null') return 1
      return a.label.localeCompare(b.label)
    })
  })

  return { options }
}

export function useOrganizationIdFilter(): {
  options: Ref<MultiselectOption[]>
} {
  const { data: allCompanies } = useGetCompanies()

  const options = computed<MultiselectOption[]>(() => {
    const companies =
      allCompanies.value?.map((c) => ({
        label: c.name,
        value: c.organizationId,
      })) ?? []

    return companies.sort((a, b) => {
      if (a.value === 'null') return -1
      if (b.value === 'null') return 1
      return a.label.localeCompare(b.label)
    })
  })

  return { options }
}
